/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

// Internal dev-host suffixes shared by the brand group and the eac/lore branch below.
const internalHostSuffixes = String.raw`-(ci|gamedev)\.ol\.epicgames\.net|\.cosmos\.marketing\.dev\.on\.epicgames\.com`;

/**
 * eac and lore are kept out of the shared brand group because eac.com and
 * lore.com are not Epic-controlled domains — only easy.ac / lore.org and
 * their internal dev hosts match.
 */
export const epicOriginRegex = new RegExp(
	String.raw`^(((www|www2|localhost|rossi|dev|docs|forums|company|legal|status|onlineservices)(\.staging)?\.)?(unrealengine|epicgames|twinmotion|metahuman|realityscan|kidswebservices|fab|quixel)(\.com|${internalHostSuffixes})|((www|prod-staging)\.)?easy\.ac|(www\.)?lore\.org|(eac|lore)(${internalHostSuffixes}))\/?`,
);

/**
 * Characters that browsers silently strip from URLs during normalization
 * (per WHATWG URL spec: U+0009 TAB, U+000A LF, U+000D CR), plus other C0
 * control characters and U+007F DEL that have no business in a redirect
 * target. Any of these in untrusted input must reject the URL outright:
 * a path like "/\t\n\r/evil.com" looks safe to a naive `startsWith("//")`
 * check but the browser normalizes it to "//evil.com" before navigation,
 * producing a protocol-relative URL and an open redirect.
 *
 * Companion mitigation to the backslash bypass (H1-3511732).
 */
const URL_FORBIDDEN_CHARS_REGEX = /[\u0000-\u001F\u007F]/;

/**
 * Checks if href is a safe relative path (starts with / but not //).
 * Rejects paths containing characters that bypass naive prefix checks but
 * get stripped or rewritten by the browser before navigation:
 *   - backslash — e.g. /\evil.com normalizes to //evil.com (H1-3511732)
 *   - C0 controls / DEL — e.g. /\t\n\r/evil.com normalizes to //evil.com
 */
export const isRelative = (href?: string): boolean => {
	if (!href) return false;
	if (URL_FORBIDDEN_CHARS_REGEX.test(href)) return false;
	return href.startsWith("/") && !href.startsWith("//") && !href.includes("\\");
};

export const isHash = (href?: string): boolean => {
	return Boolean(href?.startsWith("#"));
};

export const isSameOrigin = (href = ""): boolean => {
	if (!href) return false;

	// Reject any input containing characters that browsers strip during
	// URL normalization (tab/CR/LF) or other C0 controls. Without this,
	// `new URL()` silently normalizes them away and a hostile hostname
	// like "www.unrealengine.co\nm" would parse to the trusted host.
	if (URL_FORBIDDEN_CHARS_REGEX.test(href)) return false;

	if (isRelative(href)) {
		return true;
	}

	if (isHash(href)) {
		return true;
	}

	// For absolute URLs, only allow http:// or https:// protocols
	// Reject all other protocols (javascript:, data:, vbscript:, etc.)
	try {
		let cleanedHref = href;
		if (cleanedHref.startsWith("//")) {
			cleanedHref = `https:${cleanedHref}`;
		}

		const url = new URL(cleanedHref);

		// Only allow http and https protocols - reject all others
		// This automatically blocks javascript:, data:, vbscript:, file:, etc.
		if (url.protocol !== "http:" && url.protocol !== "https:") {
			return false;
		}

		const matches = url.hostname.match(epicOriginRegex);
		const sameOrigin = (matches && matches.length && matches[0].length === url.hostname.length) || false;
		return sameOrigin;
	} catch {
		return false;
	}
};

/**
 * Checks if a URL is absolute (starts with http/https or protocol-relative //)
 * @param href - The URL to check
 * @returns boolean - True if URL is absolute
 */
export const isAbsolute = (href?: string): boolean => {
	return Boolean(href?.indexOf("http") === 0) || Boolean(href?.startsWith("//"));
};

/**
 * Checks if a URL is a mailto link
 * @param href - The URL to check
 * @returns boolean - True if URL is a mailto link
 */
export const isMailTo = (href?: string): boolean => {
	return Boolean(href?.indexOf("mailto:") === 0);
};

/**
 * Checks if a URL is an Epic Games Launcher link
 * @param href - The URL to check
 * @returns boolean - True if URL is a launcher link
 */
export const isLauncher = (href?: string): boolean => {
	return Boolean(href?.indexOf("com.epicgames.launcher:") === 0);
};

/**
 * Checks if a pathname contains a locale prefix
 * @param locales - Array of locale codes to check for
 * @param href - The URL/pathname to check
 * @returns boolean - True if pathname has a locale prefix
 */
export const pathnameHasLocale = (locales: string[], href?: string): boolean => {
	if (!href || !locales?.length) return false;

	if (href.startsWith("/api/")) return true;

	return locales.some((locale) => href.startsWith(`/${locale}/`) || href === `/${locale}`);
};

/**
 * Strips the leading locale segment from a pathname.
 * E.g., "/de/download" → "/download", "/en-US" → "/".
 * Returns the path unchanged if it has no locale prefix.
 */
export const stripLocalePrefix = (locales: string[], href: string): string => {
	// Don't touch /api/ paths — pathnameHasLocale returns true for them as a special case
	if (href.startsWith("/api/")) return href;
	// Only strip if the first segment is an actual locale
	const firstSegment = href.split("/")[1];
	if (!firstSegment || !locales.includes(firstSegment)) return href;
	const segments = href.split("/");
	segments.splice(1, 1);
	return segments.join("/") || "/";
};

/**
 * Strips a basePath prefix from a pathname so that locale processing can
 * operate on the route path alone.
 *
 * Sites served under a sub-path (e.g. Fab at `/o`) include the basePath in
 * resolved anchor hrefs.  Without stripping it first, locale insertion
 * produces `/en-US/o/page` instead of the correct `/o/en-US/page`.
 *
 * @param pathname - Full pathname that may start with basePath (e.g. "/o/become-a-publisher")
 * @param basePath - The site basePath to strip (e.g. "/o"). Empty string means no basePath.
 * @returns `{ routePath, basePath }` where routePath always starts with "/"
 *
 * @example
 * stripBasePath("/o/become-a-publisher", "/o")
 * // => { routePath: "/become-a-publisher", basePath: "/o" }
 *
 * @example
 * stripBasePath("/become-a-publisher", "")
 * // => { routePath: "/become-a-publisher", basePath: "" }
 */
export const stripBasePath = (pathname: string, basePath: string): { routePath: string; basePath: string } => {
	if (basePath && pathname.startsWith(`${basePath}/`)) {
		return { routePath: pathname.slice(basePath.length), basePath };
	}
	if (basePath && pathname === basePath) {
		return { routePath: "/", basePath };
	}
	return { routePath: pathname, basePath: "" };
};

export interface RoutingPathCandidates {
	/** Full path — base path included — to navigate to when the target is not this app. */
	localePathname: string;
	/** Paths to test against `next.config` redirect sources, most specific first. */
	redirectPaths: string[];
	/** Paths to test against the published routing rules, most specific first. */
	rulePaths: string[];
}

/**
 * Derives the paths a clicked in-app href should be matched against when deciding
 * whether the destination belongs to this app (client-side nav) or to another
 * origin (hard nav).
 *
 * Next.js prefixes every relative href with basePath, so a click on epicgames'
 * `/news/x` resolves to `/site/news/x`. Whether the published routing rule carries
 * that prefix depends on the edge router: epicgames sets `BASE_PATH=/site`, so the
 * worker strips it before matching and its rules are base-path relative
 * (`^(/locale)?/news(/|$)`), while fab keeps the prefix in the rule (`^/o(/.*)?$`).
 * Both forms are therefore returned — prefixed first, then stripped. Without the
 * stripped form, every internal link on a BASE_PATH site misses its rule and falls
 * back to a full page load.
 *
 * @param pathname - Resolved pathname of the clicked link (may include the base path)
 * @param siteBasePath - The site base path (e.g. "/site"); empty string means none
 * @param locale - Current locale code (e.g. "en-US")
 * @param locales - Allowlist of supported locale codes
 * @param queryParamMode - True when the site carries locale as `?lang=` instead of a path prefix
 *
 * @example
 * getRoutingPathCandidates("/site/news/x", "/site", "en-US", ["en-US"], true)
 * // => { localePathname: "/site/news/x", redirectPaths: ["/site/news/x", "/news/x"],
 * //      rulePaths: ["/site/news/x", "/news/x"] }
 */
export const getRoutingPathCandidates = (
	pathname: string,
	siteBasePath: string,
	locale: string,
	locales: string[],
	queryParamMode: boolean,
): RoutingPathCandidates => {
	const { routePath, basePath } = stripBasePath(pathname, siteBasePath);

	let localePathname = pathname;
	let strippedPathname = routePath;
	if (!queryParamMode && !pathnameHasLocale(locales, routePath)) {
		// Insert locale after basePath so /o/page becomes /o/en-US/page
		// instead of /en-US/o/page
		localePathname = `${basePath}/${locale}${routePath}`;
		strippedPathname = `/${locale}${routePath}`;
	}

	// basePath is "" unless the prefix was actually present, so sites without one
	// keep a single candidate and behave exactly as before.
	const hasBasePath = Boolean(basePath);

	return {
		localePathname,
		redirectPaths: hasBasePath ? [pathname, routePath] : [pathname],
		rulePaths: hasBasePath ? [localePathname, strippedPathname] : [localePathname],
	};
};

/**
 * Function signature for locale-aware path generation
 */
export type GetLocalizedPathFn = (path: string, locale: string) => string;

/**
 * Default locale path generation - always prefix with locale.
 * Used as fallback when no custom getLocalizedPath is provided.
 */
export const defaultLocalePath: GetLocalizedPathFn = (path, locale) => {
	const normalizedPath = path.startsWith("/") ? path : `/${path}`;
	return `/${locale}${normalizedPath}`;
};

/**
 * Normalizes a URL href with locale prefix based on the site's locale configuration.
 * Consolidates the common pattern of checking isRelative + pathnameHasLocale + getLocalizedPath.
 *
 * @param href - The href to normalize (can contain :locale or {locale} placeholders)
 * @param locale - Current locale code (e.g., "de", "en-US")
 * @param locales - Array of supported locale codes
 * @param getLocalizedPath - Optional custom path generator (defaults to always-prefix behavior)
 * @returns The normalized href with appropriate locale prefix
 *
 * @example
 * // Default behavior (always prefix)
 * normalizeLocaleHref("/about", "de", ["en-US", "de"]) // => "/de/about"
 *
 * @example
 * // With "as-needed" mode via custom getLocalizedPath
 * normalizeLocaleHref("/about", "en-US", ["en-US", "de"], asNeededPath) // => "/about"
 *
 * @example
 * // With locale placeholder
 * normalizeLocaleHref("/:locale/about", "de", ["en-US", "de"]) // => "/de/about"
 */
export const normalizeLocaleHref = (
	href: string,
	locale: string,
	locales: string[],
	getLocalizedPath: GetLocalizedPathFn = defaultLocalePath,
): string => {
	if (!href) return href;

	// Replace locale placeholders
	let finalHref = href.replace(/:locale/g, locale).replace(/\{locale\}/g, locale);

	const queryParamMode = process.env.NEXT_PUBLIC_LOCALE_MODE === "never";

	if (isRelative(finalHref)) {
		if (queryParamMode) {
			// In "never" mode, URLs must be clean — no locale prefix.
			finalHref = stripLocalePrefix(locales, finalHref);
		} else if (!pathnameHasLocale(locales, finalHref)) {
			// In "always" or "as-needed" mode, add locale prefix if missing.
			finalHref = getLocalizedPath(finalHref, locale);
		}
	}

	return finalHref;
};

/**
 * Validates if a string is a valid URL
 * @param url - The URL string to validate
 * @returns boolean - True if URL is valid
 */
export const isValidUrl = (url?: string | URL | null): boolean => {
	if (!url) return false;
	try {
		new URL(url);
		return true;
	} catch {
		console.error("Invalid URL: ", url);
		return false;
	}
};

/**
 * Checks if a URL uses HTTPS protocol
 * @param url - The URL to check
 * @returns boolean - True if URL uses HTTPS
 */
export const isHttpsUrl = (url?: string | URL | null): boolean => {
	if (!url || !isValidUrl(url)) return false;
	if (new URL(url).protocol !== "https:") return false;
	return true;
};

/**
 * Checks if a URL matches the configured server origin
 * @param href - The URL to check
 * @returns boolean - True if URL matches server origin
 */
export const isOriginServerUrl = (href: string): boolean => {
	try {
		// Check if it's an absolute URL
		const url = new URL(href);

		// Get the configured server URL origin
		const serverUrl = process.env.NEXT_PUBLIC_EPIC_SERVER_URL;
		if (serverUrl) {
			const serverOrigin = new URL(serverUrl).origin;
			// If the URL's origin matches the server origin, return true
			if (url.origin === serverOrigin) {
				return true;
			}
		}

		// It's an absolute URL that doesn't match our origin
		return false;
	} catch {
		// If URL parsing fails, not a valid absolute URL
		return false;
	}
};
